import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useGetDeclineReasonReport } from '../../../services/loanService';
import { DECLINE_STAGE_LABELS, declineReasonLabel } from '../../../constants/declineReasons';
import { formatCurrency, formatDate } from '../../../utils/formatters';
import { toLocalDateString } from '../../../utils/localDate';

const firstOfMonth = () => {
  const today = new Date();
  return toLocalDateString(new Date(today.getFullYear(), today.getMonth(), 1));
};

const stageLabel = (stage?: string) => (stage && DECLINE_STAGE_LABELS[stage]) || stage || '—';

/** CSV-safe value: quoted, with inner quotes doubled. */
const csv = (value: unknown) => `"${String(value ?? '').replace(/"/g, '""')}"`;

/**
 * Why business is lost: applications declined in a period, by reason and by the stage they were
 * declined at, with the list behind the totals and a CSV download.
 */
const DeclineReasonsReport: React.FC = () => {
  const [fromDate, setFromDate] = useState(firstOfMonth());
  const [toDate, setToDate] = useState(toLocalDateString());
  const { data: report, isLoading, isError } = useGetDeclineReasonReport({ fromDate, toDate });

  const downloadCsv = () => {
    if (!report?.applications) return;
    const header = ['Application', 'Customer', 'Amount requested', 'Declined at', 'Reason', 'Remarks', 'Declined by', 'Date'];
    const rows = report.applications.map(a => [
      a.applicationNumber, a.customerName, a.requestedAmount, stageLabel(a.stage), declineReasonLabel(a.reason),
      a.remarks, a.declinedBy, a.declinedAt ? formatDate(a.declinedAt) : ''
    ]);
    const content = [header, ...rows].map(r => r.map(csv).join(',')).join('\r\n');
    const url = URL.createObjectURL(new Blob([content], { type: 'text/csv;charset=utf-8' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `decline-reasons_${fromDate}_to_${toDate}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const total = report?.totalDeclined || 0;

  return (
    <div className="container-fluid px-xl-4 px-sm-2 px-0 py-3">
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
        <div>
          <Link to="/reports" className="text-secondary small"><i className="fa fa-arrow-left me-1"></i>Reports</Link>
          <h4 className="mb-0">Decline reasons</h4>
          <small className="text-muted">Applications declined at verification, credit assessment or Credit Committee</small>
        </div>
        <div className="d-flex align-items-end gap-2 flex-wrap">
          <div>
            <label className="form-label small mb-1" htmlFor="declineFrom">From</label>
            <input id="declineFrom" type="date" className="form-control form-control-sm" value={fromDate}
              max={toDate} onChange={e => setFromDate(e.target.value)} />
          </div>
          <div>
            <label className="form-label small mb-1" htmlFor="declineTo">To</label>
            <input id="declineTo" type="date" className="form-control form-control-sm" value={toDate}
              min={fromDate} onChange={e => setToDate(e.target.value)} />
          </div>
          <button type="button" className="btn btn-sm btn-outline-primary" onClick={downloadCsv} disabled={!total}>
            <i className="fa fa-download me-1"></i>CSV
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="text-center py-5"><div className="spinner-border text-primary" role="status"></div></div>
      ) : isError ? (
        <div className="alert alert-danger">The report could not be loaded.</div>
      ) : (
        <>
          <div className="row g-3 mb-3">
            <div className="col-md-4">
              <div className="card"><div className="card-body">
                <div className="text-muted small">Applications declined</div>
                <div className="h3 mb-0">{total}</div>
              </div></div>
            </div>
            <div className="col-md-4">
              <div className="card"><div className="card-body">
                <div className="text-muted small">Amount requested on them</div>
                <div className="h3 mb-0">{formatCurrency(report?.totalRequested || 0)}</div>
              </div></div>
            </div>
            <div className="col-md-4">
              <div className="card"><div className="card-body">
                <div className="text-muted small">Top reason</div>
                <div className="h5 mb-0">{report?.byReason?.[0] ? declineReasonLabel(report.byReason[0].reason) : '—'}</div>
              </div></div>
            </div>
          </div>

          <div className="row g-3 mb-3">
            <div className="col-lg-7">
              <div className="card h-100">
                <div className="card-header py-2"><h6 className="mb-0">By reason</h6></div>
                <div className="table-responsive">
                  <table className="table table-sm mb-0">
                    <thead><tr><th>Reason</th><th className="text-end">Applications</th><th className="text-end">Share</th><th className="text-end">Amount requested</th></tr></thead>
                    <tbody>
                      {(report?.byReason || []).map(r => (
                        <tr key={r.reason}>
                          <td>{declineReasonLabel(r.reason)}</td>
                          <td className="text-end">{r.count}</td>
                          <td className="text-end">{total ? Math.round(((r.count || 0) / total) * 100) : 0}%</td>
                          <td className="text-end">{formatCurrency(r.requestedAmount || 0)}</td>
                        </tr>
                      ))}
                      {!report?.byReason?.length && <tr><td colSpan={4} className="text-muted">No declines in this period.</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            <div className="col-lg-5">
              <div className="card h-100">
                <div className="card-header py-2"><h6 className="mb-0">By stage</h6></div>
                <div className="table-responsive">
                  <table className="table table-sm mb-0">
                    <thead><tr><th>Declined at</th><th className="text-end">Applications</th></tr></thead>
                    <tbody>
                      {(report?.byStage || []).map(s => (
                        <tr key={s.stage}><td className="text-capitalize">{stageLabel(s.stage)}</td><td className="text-end">{s.count}</td></tr>
                      ))}
                      {!report?.byStage?.length && <tr><td colSpan={2} className="text-muted">—</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-header py-2"><h6 className="mb-0">Declined applications</h6></div>
            <div className="table-responsive">
              <table className="table table-sm table-hover mb-0">
                <thead>
                  <tr><th>Application</th><th>Customer</th><th className="text-end">Requested</th><th>Declined at</th><th>Reason</th><th>Remarks</th><th>By</th><th>Date</th></tr>
                </thead>
                <tbody>
                  {(report?.applications || []).map(a => (
                    <tr key={a.applicationId}>
                      <td>{a.applicationId ? <Link to={`/application/${a.applicationId}`}>{a.applicationNumber}</Link> : a.applicationNumber}</td>
                      <td>{a.customerName}</td>
                      <td className="text-end">{formatCurrency(a.requestedAmount || 0)}</td>
                      <td className="text-capitalize">{stageLabel(a.stage)}</td>
                      <td>{declineReasonLabel(a.reason)}</td>
                      <td className="small text-muted" style={{ maxWidth: 280 }}>{a.remarks}</td>
                      <td className="small">{a.declinedBy}</td>
                      <td className="small">{a.declinedAt ? formatDate(a.declinedAt) : ''}</td>
                    </tr>
                  ))}
                  {!report?.applications?.length && <tr><td colSpan={8} className="text-muted">No declines in this period.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default DeclineReasonsReport;
